import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight, AlertCircle, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

export default function AdminLogin() {
  const { token, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState({ type: "idle", message: "" });

  if (loading) return <FullPageLoader />;
  if (token) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setStatus({ type: "error", message: "Email and password are required." });
      return;
    }
    setStatus({ type: "loading", message: "" });
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setStatus({ type: "error", message: msg });
    }
  };

  return (
    <div className="min-h-screen bg-[#F4FAF8] flex items-center justify-center px-4">
      <div className="absolute inset-0 -z-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#5BAE71]/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#E8A33D]/15 blur-3xl" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="flex items-center justify-center gap-2.5 mb-7">
          <img
            src="https://customer-assets.emergentagent.com/job_design-upgrade-217/artifacts/fzqlgxrv_App%20icon.png"
            alt="Careable"
            className="w-12 h-12 rounded-2xl object-cover"
          />
          <div className="leading-tight">
            <p className="font-display font-extrabold text-[#1F4E5F] text-xl">Careable</p>
            <p className="text-[10px] font-bold tracking-[0.18em] text-[#E8A33D] uppercase">
              Admin Console
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-white border border-[#C9DDD6] rounded-3xl p-7 sm:p-9 shadow-[0_18px_50px_rgba(15,56,32,0.05)] space-y-4"
        >
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0E2A36]">Sign in</h1>
            <p className="text-sm text-[#6A8290] mt-1">
              Restricted area. Authorised staff only.
            </p>
          </div>

          <Field label="Email" id="ad-email" icon={Mail}>
            <input
              id="ad-email"
              type="email"
              data-testid="admin-login-email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full pl-10 pr-4 py-3 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
            />
          </Field>

          <Field label="Password" id="ad-pw" icon={Lock}>
            <input
              id="ad-pw"
              type="password"
              data-testid="admin-login-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full pl-10 pr-4 py-3 bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl text-[15px] focus:outline-none focus:border-[#1F4E5F] focus:ring-2 focus:ring-[#5BAE71]/30 transition"
            />
          </Field>

          {status.type === "error" && (
            <div
              data-testid="admin-login-error"
              className="flex items-center gap-2 text-[#C84444] bg-[#E55B5B]/10 border border-[#E55B5B]/25 rounded-xl px-3 py-2.5 text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{status.message}</span>
            </div>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={status.type === "loading"}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] px-6 py-3.5 rounded-full text-[15px] font-semibold transition-all hover:gap-3 disabled:opacity-70"
          >
            {status.type === "loading" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
              </>
            ) : (
              <>
                Sign in <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <p className="text-xs text-center text-[#6A8290] mt-5">
          <Link
            to="/admin/forgot-password"
            data-testid="admin-forgot-link"
            className="font-semibold text-[#1F4E5F] hover:underline"
          >
            Forgot your password?
          </Link>
        </p>
      </motion.div>
    </div>
  );
}

function Field({ label, id, icon: Icon, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-bold uppercase tracking-[0.14em] text-[#3D5560] mb-2"
      >
        {label}
      </label>
      <div className="relative">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A8290]" />
        {children}
      </div>
    </div>
  );
}

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4FAF8]">
      <Loader2 className="w-6 h-6 animate-spin text-[#1F4E5F]" />
    </div>
  );
}
